import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import OfferPageAssistantHost from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantHost'
import { OfferPageAssistantSourceRegistration } from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantSurfaceRegistry'
import {
  createOfferPageSnapshotSelection,
  type FlashCharterItinerary,
  type FlashCharterItineraryLeg,
  type FlashOfferPageSnapshotSource,
} from 'tripPlanner/assistant/surfaces/offerPage/offerPageSnapshotTypes'
import useOfferPageRoomPackageLoyaltyEarnTotals from 'tripPlanner/assistant/surfaces/offerPage/useOfferPageRoomPackageLoyaltyEarnTotals'
import OfferPageAssistantExpandedContext from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantExpandedContext'
import type { PropertyRoomRateViewForAssistant } from 'tripPlanner/assistant/surfaces/offerPage/offerPageContextTypes'
import type { OfferPageState, OfferPageDispatchAction } from 'contexts/OfferPage/offerPageStateReducer'
import { INITIAL_OFFER_PAGE_STATE, OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import type { ReducerMiddleware } from 'lib/redux/reducerUtils'
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getInitialLeOfferPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { getDurationPackageOptions, getEffectiveCharterBundleRoute } from 'selectors/offerPage/offerPageSelectors'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import accommodationMobileFilterMiddleware from 'components/OfferPage/Accommodation/AccommodationMobileFilterMiddleware'
import { getNextFilterWithCapacityCheckInMiddleware } from '../utils'
import { getFeederOriginAirportCode } from 'lib/charterFlights/charterBundleOfferPageUtils'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import { FlightsClassTypes, isRewardFareClass, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import useCharterFareClassPolicy from 'hooks/Flights/useCharterFareClassPolicy'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import useCheapestAvailableReward from 'hooks/OfferPage/useCheapestAvailableReward'
import FlashHotelContent from './FlashHotelContent'
import FlashPackageCharterFallbackPricingRegistration, { type FlashPackageCharterFallbackPricingResult } from './FlashPackageCharterFallbackPricingRegistration'
import { isRenderablePackageView } from './SameRoom/FlashHotelSameRoomOption'
import {
  getFirstAvailableDuration,
  getFlashCalendarSkipDurationOptions,
  shouldSkipFlashDurationStep,
} from './flashHotelCalendarSkipUtils'
import useMountedEffect from 'hooks/useMountedEffect'
import { offerPageDurationChangeEvent, offerPageGuestSelectionEvent, offerPageHotLeadEvent } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import multiRoomsMobileFilterMiddleware from '../MultipleRoomsBooking/Mobile/MobileMultipleRoomsFilterMiddleware'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import usePrefetchFlightsFromOffer from '../hooks/prefetchFlights/usePrefetchFlightsFromOffer'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { isCharterEnabled } from 'selectors/featuresSelectors'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import getEffectiveHotelFlightPrice from 'lib/offer/getEffectiveHotelFlightPrice'

const mobileVariantMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [accommodationMobileFilterMiddleware]
const mobileNewMultiRoomMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [multiRoomsMobileFilterMiddleware]
const defaultMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddleware]
const defaultRooms = [generateOfferPageRoom()]
const offerPageReducerOptions = { syncAirportDefault: true }
const EMPTY_HOTEL_PACKAGE_VIEWS: Array<App.OfferPackageOptionView> = []
const EMPTY_BEDBANK_PACKAGE_VIEWS: Array<App.BedbankOfferPackageView> = []
const EMPTY_PROPERTY_ROOM_RATE_VIEWS: Array<PropertyRoomRateViewForAssistant> = []
const EMPTY_FARE_CLASSES: ReadonlyArray<FareClassWithReward> = []

// `flights` is always non-stop for charter routes (a direct MEL<->MLE journey), but `numberOfStops`
// is read rather than assumed so this can't silently misreport if that ever changes.
export function extractCharterItineraryLeg(journey: App.JourneyV2 | undefined): FlashCharterItineraryLeg | undefined {
  if (!journey) {
    return undefined
  }

  const firstFlight = journey.flightGroup.flights.at(0)
  const lastFlight = journey.flightGroup.flights.at(-1)
  if (!firstFlight || !lastFlight) {
    return undefined
  }

  return {
    departureAirportCode: firstFlight.departureAirport,
    departureDateTime: firstFlight.departureDateTime,
    arrivalAirportCode: lastFlight.arrivalAirport,
    arrivalDateTime: lastFlight.arrivalDateTime,
    isDirect: journey.flightGroup.numberOfStops === 0,
    durationHours: journey.flightGroup.totalFlightDuration.hours,
    durationMinutes: journey.flightGroup.totalFlightDuration.minutes,
  }
}

// Both a check-in and check-out date must be picked -- with no dates, the underlying hook still
// returns its first-available journey as a placeholder (the same way the real page's own
// calendar-driven components behave before a date is chosen), which would misrepresent an arbitrary
// date as "the" itinerary. Matches `ReturnCharterFlightTile`'s own empty state otherwise.
export function computeCharterItinerary(
  hasBothDates: boolean,
  departingJourney: App.JourneyV2 | undefined,
  returningJourney: App.JourneyV2 | undefined,
): FlashCharterItinerary | undefined {
  if (!hasBothDates) {
    return undefined
  }

  const departing = extractCharterItineraryLeg(departingJourney)
  const returning = extractCharterItineraryLeg(returningJourney)
  return departing && returning ? { departing, returning } : undefined
}

interface Props {
  offer: App.HotelOffer
}

interface FlashOfferAssistantSourceRegistrationProps {
  offer: App.HotelOffer
  offerPageState: OfferPageState
}

function FlashOfferAssistantSourceRegistration({
  offer,
  offerPageState,
}: FlashOfferAssistantSourceRegistrationProps) {
  const currencyCode = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const packageViews = useAppSelector(appState => getDurationPackageOptions(offerPageState, appState))
  // Matches `useOfferInclusions`'s own gating exactly, so the snapshot only claims LuxPlus+ tier
  // inclusions the real tile would actually show a subscribed customer.
  const inclusionsViewableLuxPlusTier = useAppSelector(getInclusionsViewableLuxPlusTier)
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const luxPlusInclusionsTier = isLuxPlusEnabled ? inclusionsViewableLuxPlusTier : undefined
  const isInclusionsValidityEnabled = useAppSelector(appState => !!appState.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.croInclusionValidity])
  // Loyalty-earn totals require a real API call (`useLoyaltyPointsEarnCalculator`) -- only compute
  // them once the assistant is actually expanded, rather than firing on every page load.
  const isAssistantExpanded = useContext(OfferPageAssistantExpandedContext)
  const charterBundleRoute = getEffectiveCharterBundleRoute(offerPageState)
  const isCharterModeEnabled = useAppSelector(isCharterEnabled)
  // Same expanded-gating as the loyalty totals above -- `useCharterFlightDates` can trigger a real
  // charter flight-search fetch, so only resolve it once the assistant is actually expanded rather
  // than on every page load. `FlashHotelRateOption` already calls this hook with the same params
  // per rendered rate option, so this shares its Redux cache key rather than adding a new one.
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: offerPageState.checkInDate,
    nights: offerPageState.duration,
    charterBundleRoute: isAssistantExpanded ? charterBundleRoute : undefined,
    rooms: offerPageState.rooms,
    fareClass: offerPageState.fareClass,
  })
  // Same expanded-gating as the loyalty totals above -- reuses `FlashHotelOptionPriceDetails`'s own
  // Redux cache key (dates/duration/occupants/airport), so this doesn't add a second fetch beyond
  // whatever the real price panel already triggers once rendered.
  const {
    totalFlightPrice,
    loading: effectiveFlightPriceLoading,
    loyaltyEarnKey: flightLoyaltyEarnKey,
    rewardAvailable,
    rewardSeatPointsTotal,
    hasReturnFareFree,
  } = useOfferPageFlightPrice({ disabled: !isAssistantExpanded })
  // Matches `FlashHotelOptionPriceDetails.tsx`'s own `isRewardSelected` exactly: true when the
  // customer is paying for the flight seat itself with Société points rather than cash, in which
  // case the real page's flight-bundle earn request is skipped too (no cash spend on that leg to
  // earn points against).
  const isRewardSelected = rewardAvailable && rewardSeatPointsTotal != null
  // The real, live flight price -- not `packageView.flightPrice`, a static calendar-level
  // indicative sample fare the real page stops using entirely once real dates are picked (see
  // `getEffectiveHotelFlightPrice`'s own `!checkIn` fallback condition). Matches
  // `FlashHotelOptionPriceDetails.tsx`'s `effectiveFlightPrice` exactly. Deliberately uses the
  // ungated `charterBundleRoute` above (not the `isAssistantExpanded`-gated one passed to
  // `useCharterFlightDates`) -- safe because `useOfferPageFlightPrice`'s own `disabled` flag
  // already zeroes `totalFlightPrice` while collapsed, so this can't fire an extra fetch either way.
  const effectiveFlightPrice = getEffectiveHotelFlightPrice({
    checkIn: offerPageState.checkInDate,
    charterBundleRoute,
    flightOriginCode: offerPageState.airport?.code,
    offer,
    totalFlightPrice,
  })
  // Matches `FlashHotelOptionPriceDetails.tsx`'s own `shouldUseFallbackPricing` exactly: with no
  // charter route this is always false; otherwise fallback pricing is needed whenever there's no
  // check-in date, or the check-in date doesn't correspond to a real charter departure.
  const isCheckInMatchingCharterDate = !charterBundleRoute || (!!offerPageState.checkInDate && !!charterDepartDate)
  const shouldUseCharterFallbackPricing = !!charterBundleRoute && (!offerPageState.checkInDate || !isCheckInMatchingCharterDate)
  const feederOriginAirportCode = getFeederOriginAirportCode(charterBundleRoute, offerPageState.airport)
  // `FlashHotelSameRoomOption.tsx` never mounts a `FlashHotelOptionPriceDetails` (and therefore
  // never calls `useCharterBundleFallbackPricing`) for a package that isn't renderable -- matching
  // that filter here means this loop only fetches for packages the real page would fetch for too,
  // rather than a package that's sold out or capacity-invalid firing a fetch the real page never
  // triggers at all.
  const requiresRates = !!offer.property?.useDynamicCancellationPolicies && !!offerPageState.checkInDate
  const renderableFallbackPricingPackageViews = useMemo(
    () => packageViews.filter(packageView => isRenderablePackageView(packageView, requiresRates)),
    [packageViews, requiresRates],
  )
  // `useOfferPageFlightPrice` above only threads a feeder origin into its own internal
  // `useCharterFlightPrice` call for Economy (`shouldUseFeederEstimatedPriceFromCharter` requires
  // `toBaseFareClass(fareClass) === ECONOMY`, useFlightPrice.ts) -- passing the ungated
  // `feederOriginAirportCode` above for any other cabin would silently diverge from that call's
  // cache key (`useAllCharterFlightJourneys` keys on it), firing a genuinely new fetch instead of
  // reusing one. Gated here to guarantee the same key regardless of the selected fare class.
  const itineraryFeederOriginAirportCode = toBaseFareClass(offerPageState.fareClass) === FlightsClassTypes.ECONOMY ? feederOriginAirportCode : undefined
  // Same params (and so the same Redux cache key) `useCharterFlightPrice` passes internally to this
  // exact hook -- reuses whatever `useOfferPageFlightPrice` above already triggered rather than
  // firing a second fetch. `toBaseFareClass` matches `useCharterFlightPrice`'s own conversion, so a
  // reward fare class selection can't produce a different cache key than the live price call uses.
  const { departingJourney: charterDepartingJourney, returningJourney: charterReturningJourney } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute,
    checkInDate: offerPageState.checkInDate,
    checkOutDate: offerPageState.checkOutDate,
    occupancies: offerPageState.rooms,
    fareClass: toBaseFareClass(offerPageState.fareClass),
    disabled: !isAssistantExpanded,
    feederOriginAirportCode: itineraryFeederOriginAirportCode,
  })
  const hasBothCharterDates = !!offerPageState.checkInDate && !!offerPageState.checkOutDate
  // `computeCharterItinerary` builds a fresh object every call -- memoized so `charterItinerary`
  // only gets a new reference when its actual inputs change, rather than defeating the `source`
  // `useMemo` below (which depends on it) every render regardless of reason.
  const charterItinerary = useMemo(
    () => computeCharterItinerary(hasBothCharterDates, charterDepartingJourney, charterReturningJourney),
    [hasBothCharterDates, charterDepartingJourney, charterReturningJourney],
  )
  // Both pure derivations (campaign tags + occupancy math + feature flags) -- no fetch, so unlike
  // the hooks above these aren't `isAssistantExpanded`-gated. Matches the exact hooks
  // `MaldivesCharterFlightFareClassModal`'s own card set uses, but deliberately stops at the static
  // catalog (names/inclusions/baggage) -- live cross-cabin cash/reward pricing is only ever fetched
  // by that modal once a customer opens it (a distinct request per cabin), so pre-fetching it here
  // would mean new network calls for cabins the customer may never ask about.
  const charterFareClassPolicy = useCharterFareClassPolicy(offer, offerPageState.rooms)
  const charterSelectableFareClasses = useMaldivesCharterModalFareClasses(charterFareClassPolicy.allowedFareClasses)
  // Matches `FlashHotelRewardsAvailableCard`'s own hook call exactly (same params) when it's
  // mounted, so this shares its cache rather than adding a new fetch in that case. That card is
  // only conditionally rendered per CTA branch though (absent while loading, insufficient-points,
  // etc.), so unlike the itinerary hook above this isn't guaranteed to already be firing --
  // `useCheapestAvailableReward` itself takes no `disabled` param, so the same expanded-gating
  // every other fetch-triggering hook in this component uses is applied here by starving its own
  // internal `allows()` check (`hasDates && hasLoyaltyAccount && allowedFareClasses.includes(...)`)
  // with an empty fare-class list while collapsed, rather than skipping the call outright.
  const { cheapest: cheapestAvailableReward } = useCheapestAvailableReward({
    checkInDate: offerPageState.checkInDate,
    checkOutDate: offerPageState.checkOutDate,
    occupancies: offerPageState.rooms,
    allowedFareClasses: isAssistantExpanded ? charterSelectableFareClasses : EMPTY_FARE_CLASSES,
  })
  // Matches the real card's own suppression exactly: no upsell once the customer is already on a
  // reward fare (nothing left to upsell them to).
  const charterCheapestAvailableReward = isRewardFareClass(offerPageState.fareClass) ? undefined : cheapestAvailableReward ?? undefined
  const [charterFallbackPricingByPackage, setCharterFallbackPricingByPackage] = useState<Record<string, FlashPackageCharterFallbackPricingResult>>({})
  const handleCharterFallbackPricingResolved = useCallback((packageUniqueKey: string, result: FlashPackageCharterFallbackPricingResult | undefined) => {
    setCharterFallbackPricingByPackage(previous => {
      if (!result && !(packageUniqueKey in previous)) {
        // Avoids an avoidable re-render: every package reports "still resolving" (undefined) on
        // its first mount, before any of them have a real price yet.
        return previous
      }

      const next = { ...previous }
      if (result) {
        next[packageUniqueKey] = result
      } else {
        delete next[packageUniqueKey]
      }
      return next
    })
  }, [])
  const { totalsByGroupKey: roomPackageLoyaltyEarnTotals, status: loyaltyStatus } = useOfferPageRoomPackageLoyaltyEarnTotals({
    offerPageState,
    currentRegionCode,
    hotelPackageViews: isAssistantExpanded ? packageViews : EMPTY_HOTEL_PACKAGE_VIEWS,
    bedbankPackageViews: EMPTY_BEDBANK_PACKAGE_VIEWS,
    propertyRoomRateViews: EMPTY_PROPERTY_ROOM_RATE_VIEWS,
    propertyIsUltraLux: false,
    effectiveFlightPrice,
    shouldUseCharterFallbackPricing,
    isRewardSelected,
    flightLoyaltyEarnKey,
  })
  const source = useMemo<FlashOfferPageSnapshotSource>(() => ({
    key: `offer:${offer.id}`,
    variant: 'flash',
    selection: createOfferPageSnapshotSelection(offerPageState),
    priceFormatContext: {
      currencyCode,
      regionCode: currentRegionCode,
    },
    offer,
    packageViews,
    roomPackageLoyaltyEarnTotals,
    loyaltyStatus,
    luxPlusInclusionsTier,
    isInclusionsValidityEnabled,
    charterBundleRoute,
    charterDepartDate,
    isCharterModeEnabled,
    effectiveFlightPrice,
    effectiveFlightPriceLoading,
    shouldUseCharterFallbackPricing,
    charterFallbackPricingByPackage,
    hasReturnFareFree,
    feederOriginAirportCode,
    charterItinerary,
    charterSelectableFareClasses,
    charterCheapestAvailableReward,
    isLuxPlusEnabled,
    luxPlusAccess: offer.luxPlus.access,
  }), [currencyCode, currentRegionCode, offer, offerPageState, packageViews, roomPackageLoyaltyEarnTotals, loyaltyStatus, luxPlusInclusionsTier, isInclusionsValidityEnabled, charterBundleRoute, charterDepartDate, isCharterModeEnabled, effectiveFlightPrice, effectiveFlightPriceLoading, shouldUseCharterFallbackPricing, charterFallbackPricingByPackage, hasReturnFareFree, feederOriginAirportCode, charterItinerary, charterSelectableFareClasses, charterCheapestAvailableReward, isLuxPlusEnabled])

  return <>
    {charterBundleRoute && shouldUseCharterFallbackPricing && isAssistantExpanded && renderableFallbackPricingPackageViews.map(packageView => (
      <FlashPackageCharterFallbackPricingRegistration
        key={packageView.package.uniqueKey}
        offer={offer}
        rooms={offerPageState.rooms}
        charterBundleRoute={charterBundleRoute}
        fareClass={offerPageState.fareClass}
        nights={packageView.package.duration}
        packageUniqueKey={packageView.package.uniqueKey}
        feederOriginAirportCode={feederOriginAirportCode}
        onResolved={handleCharterFallbackPricingResolved}
      />
    ))}
    <OfferPageAssistantSourceRegistration source={source} />
  </>
}

function FlashHotelContainer(props: Props) {
  const { offer } = props
  const [middlewares, setMiddlewares] = useState(defaultMiddlewares)
  const defaults = useAppSelector(state => getInitialLeOfferPageState(state, offer))

  // This size MUST be kept in sync with the size prop for rendering `FlashHotelSidebarFilters`
  const isMobileSize = useScreenSizeOnly('mobile')
  const defaultOfferDuration = offer.lowestPricePackage?.duration
  const offerStateForDurationOptions = useMemo(() => ({
    ...INITIAL_OFFER_PAGE_STATE,
    ...defaults,
    duration: defaults.duration ?? defaultOfferDuration ?? 0,
    offer,
  }), [defaults, defaultOfferDuration, offer])
  const durationOptions = useAppSelector(appState =>
    getFlashCalendarSkipDurationOptions(offerStateForDurationOptions, appState),
  )
  const firstAvailableDuration = useMemo(
    () => getFirstAvailableDuration(durationOptions),
    [durationOptions],
  )
  const entryDuration = defaults.duration ?? defaultOfferDuration
  const shouldPreselectDuration = shouldSkipFlashDurationStep({
    isFlashCalendarSkipEnabled: isMobileSize,
    durationSelected: defaults.durationSelected,
    currentDuration: entryDuration,
    defaultDuration: defaultOfferDuration,
    firstAvailableDuration,
  })
  const effectiveDuration = defaults.duration ?? (shouldPreselectDuration ? firstAvailableDuration : defaultOfferDuration)

  const [state, stateDispatch] = useOfferPageReducer({
    ...defaults,
    duration: effectiveDuration,
    durationSelected: defaults.durationSelected || shouldPreselectDuration,
    offer,
  }, middlewares, offerPageReducerOptions)
  useEffect(() => {
    if (!shouldPreselectDuration || firstAvailableDuration == null || state.durationSelected) {
      return
    }

    stateDispatch({ type: OfferPageActions.setDuration, duration: firstAvailableDuration })
  }, [firstAvailableDuration, shouldPreselectDuration, state.durationSelected, stateDispatch])

  const isMultiIndividualRoom = useAppSelector(appState => isMultipleRoomBookingActive(state, appState))

  usePrefetchFlightsFromOffer({
    offer,
    offerPageState: state,
    flightsEnabled: offer.offerFlightsEnabled,
  })

  useEffect(() => {
    if (isMobileSize) {
      if (isMultiIndividualRoom) {
        setMiddlewares(mobileNewMultiRoomMiddlewares)
      } else {
        setMiddlewares(mobileVariantMiddlewares)
      }
    } else {
      setMiddlewares(defaultMiddlewares)
    }
  }, [isMobileSize, isMultiIndividualRoom])

  useMountedEffect(() => {
    // the first loaded offer from an SSR is a summary. We then fetch the full version
    // This ensures the offer page state has our latest version
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useMountedEffect(() => {
    trackEvent(offerPageDurationChangeEvent(offer, state))
  }, [state.duration])

  useMountedEffect(() => {
    if (state.rooms) {
      trackEvent(offerPageGuestSelectionEvent(offer, state))
    }
  }, [state.rooms])

  const hotLead = useSaveHotLead({
    offerId: offer.id,
    offerType: offer.type,
    checkInDate: state.checkInDate,
    duration: state.duration,
    occupancy: state.rooms,
    defaultOccupancy: defaultRooms,
  })

  useEffect(() => {
    if (hotLead?.checkIn && hotLead.checkOut) {
      trackEvent(offerPageHotLeadEvent(offer, state))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  return <OfferPageStateProvider state={state} dispatch={stateDispatch}>
    <OfferPageAssistantHost offerPageState={state}>
      <FlashOfferAssistantSourceRegistration offer={offer} offerPageState={state} />
      <FlashHotelContent
        offer={offer}
      />
    </OfferPageAssistantHost>
  </OfferPageStateProvider>
}

export default memo(FlashHotelContainer)
